import { SBCRewardType } from "@/api/privateRequests/sbcs/getSbcSets";
import { ImageWithFallback } from "@/components/ImageWithFallback";
import { PointsPrice } from "@/components/organisms/SBCs/PointsPrice/PointsPrice";
import { RewardBand } from "@/components/organisms/SBCs/RewardBand";
import { Tier } from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";

import { SBCRequirements } from "./SBCRequirements";

type Props = {
  name: string;
  imageURL: string;
  reward?: SBCRewardType;
  rewardUrl: string;
  tier: Tier;
  description?: string;
  requirements: string[];
  suggestedSolution?: React.ReactNode;
  formattedPrice: string;
  pointsRequired?: number;
  sbcToggle: React.ReactNode;
};

/**
 * The same challenge alongside its solution, where the solution is the subject
 * and this is the brief: no card shell, no action, and the crest shrinks to a
 * bullet in front of the name.
 */
export const SBCSummary = ({
  name,
  imageURL,
  reward,
  rewardUrl,
  tier,
  description,
  requirements,
  suggestedSolution,
  formattedPrice,
  pointsRequired,
  sbcToggle,
}: Props) => {
  const { t } = useTranslation("common");

  return (
    <div className="cursor-auto">
      <div className="flex items-center px-4 py-3">
        <ImageWithFallback
          className="-mb-1 -ml-4 -mr-1 size-14"
          src={imageURL}
          alt={name}
          fallback={
            import.meta.env.VITE_APP_ASSETS_BASE_URL + "/misc/default-sbc.png"
          }
        />
        <div className="w-full">
          <div className="flex h-5 items-center justify-between">
            <span className="text-sm">{name}</span>
            <div className="flex items-center">{sbcToggle}</div>
          </div>
          <div className="flex h-5 items-center gap-1">
            {pointsRequired != null && (
              <PointsPrice
                points={pointsRequired}
                size={12}
                className="text-xs text-foreground-muted"
              />
            )}
            <img
              className="size-3"
              src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/coin-tmp.png`}
              alt={t("alt.coin")}
            />
            <span className="text-xs text-foreground-muted">
              {formattedPrice}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-4 mb-4 overflow-hidden rounded-lg bg-surface-raised">
        <div className="p-3">
          <SBCRequirements requirements={requirements} />
          {suggestedSolution}
        </div>
        <RewardBand
          reward={reward}
          rewardUrl={rewardUrl}
          description={description}
          tier={tier}
        />
      </div>
    </div>
  );
};
